import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Award, Camera, MapPin, Mountain, Trophy } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useSite } from "../context/SiteContext";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";

const TYPE_LABELS = {
  trip: { ru: "Поездка", en: "Journey" },
  competition: { ru: "Соревнование", en: "Competition" },
  achievement: { ru: "Достижение", en: "Achievement" },
  story: { ru: "История", en: "Story" },
  gallery: { ru: "Фотогалерея", en: "Photo gallery" },
};
const SOURCE_LABELS = {
  argali: { ru: "Экскурсия АРГАЛИ", en: "АРГАЛИ excursion" },
  other_company: { ru: "Поездка с другой компанией", en: "Trip with another company" },
  personal: { ru: "Личная поездка", en: "Personal journey" },
};

const typeIcon = (type) => {
  if (type === "competition") return Trophy;
  if (type === "achievement") return Award;
  if (type === "gallery") return Camera;
  return Mountain;
};

export default function GuideStories() {
  const { lang } = useLang();
  const site = useSite();
  const guide = site?.settings?.guide || {};
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!guide.guide_number) return;
    const params = new URLSearchParams({ guide_number: guide.guide_number });
    api.get(`/guide-journal/public?${params.toString()}`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [guide.guide_number]);

  const guideName = lang === "ru" ? (guide.name_ru || "Наталья") : (guide.name_en || guide.name_ru || "Natalia");
  const pageTitle = lang === "ru" ? `Истории и фотографии — ${guideName}` : `Stories and photos — ${guideName}`;
  const grouped = useMemo(() => items, [items]);

  return (
    <div className="min-h-screen bg-[#F3F0E8] text-[#342116]" data-testid="guide-stories-page">
      <Seo
        path="/guide/stories"
        lang={lang}
        title={pageTitle}
        description={lang === "ru" ? `Поездки, соревнования, достижения и фотографии гида ${guideName}.` : `Journeys, competitions, achievements and photographs by guide ${guideName}.`}
      />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-32 pb-12">
        <Reveal>
          <Link to="/guide" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#7e5a38] hover:text-[#A96818]"><ArrowLeft size={15} /> {lang === "ru" ? "О гиде" : "About the guide"}</Link>
          <p className="eyebrow mt-8">{lang === "ru" ? "Личный журнал гида" : "Guide journal"}</p>
          <h1 className="display display-md mt-3">{lang === "ru" ? `Истории ${guideName}` : `${guideName}'s stories`}</h1>
          <p className="max-w-3xl mt-5 text-[#5f4937] leading-relaxed">
            {lang === "ru"
              ? "Поездки по Алтаю и за его пределами, участие в соревнованиях, профессиональные события и фотографии с экскурсий. Каждая публикация проходит проверку АРГАЛИ перед появлением на сайте."
              : "Journeys across Altai and beyond, competitions, professional events and photographs from excursions. Every post is reviewed by АРГАЛИ before publication."}
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-28">
        {loading && <p className="text-[#7e6b5b]">{lang === "ru" ? "Загрузка…" : "Loading…"}</p>}
        {!loading && grouped.length === 0 && (
          <div className="border border-[#342116]/15 p-10 text-center bg-white/30">
            <Camera className="mx-auto mb-4 opacity-50" />
            <p className="font-serif text-2xl">{lang === "ru" ? "Публикации скоро появятся" : "Stories are coming soon"}</p>
          </div>
        )}

        <div className="space-y-16">
          {grouped.map((post, index) => {
            const Icon = typeIcon(post.post_type);
            const title = lang === "ru" ? post.title_ru : (post.title_en || post.title_ru);
            const body = lang === "ru" ? post.body_ru : (post.body_en || post.body_ru);
            const source = SOURCE_LABELS[post.source_context]?.[lang] || post.source_context;
            return (
              <Reveal key={post.id} delay={Math.min(index * 0.05, 0.2)}>
                <article className="border-t border-[#342116]/20 pt-8">
                  <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8 lg:gap-12">
                    <div>
                      <div className="flex items-center gap-2 text-[#A96818]"><Icon size={17} /><span className="text-[0.65rem] uppercase tracking-[0.16em]">{TYPE_LABELS[post.post_type]?.[lang]}</span></div>
                      <h2 className="font-serif text-3xl md:text-4xl mt-3">{title}</h2>
                      <div className="mt-5 space-y-2 text-sm text-[#6f5947]">
                        {post.event_date && <p>{new Intl.DateTimeFormat(lang === "ru" ? "ru-RU" : "en-GB", { day: "numeric", month: "long", year: "numeric" }).format(new Date(`${post.event_date}T00:00:00`))}</p>}
                        {post.location && <p className="flex items-center gap-2"><MapPin size={14} /> {post.location}</p>}
                        {post.organization && <p>{lang === "ru" ? "Организатор / событие" : "Organizer / event"}: {post.organization}</p>}
                        <p className="text-[#A96818]">{source}{post.source_context === "other_company" && post.source_company_name ? ` · ${post.source_company_name}` : ""}</p>
                      </div>
                    </div>
                    <div>
                      {body && <p className="text-[#493729] leading-relaxed whitespace-pre-line max-w-4xl">{body}</p>}
                      {(post.photos || []).length > 0 && (
                        <div className={`grid gap-3 mt-7 ${(post.photos || []).length === 1 ? "grid-cols-1" : "grid-cols-2 md:grid-cols-3"}`}>
                          {(post.photos || []).map((photo, i) => (
                            <figure key={photo.id} className={`${i === 0 && post.photos.length > 2 ? "md:col-span-2 md:row-span-2" : ""}`}>
                              <div className="aspect-[4/3] overflow-hidden bg-[#d9d2c5]">
                                <img src={photo.url} alt={(lang === "ru" ? photo.caption_ru : photo.caption_en) || title} className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.02]" loading="lazy" />
                              </div>
                              {(photo.caption_ru || photo.caption_en) && <figcaption className="text-xs text-[#7d6958] mt-2">{lang === "ru" ? (photo.caption_ru || photo.caption_en) : (photo.caption_en || photo.caption_ru)}</figcaption>}
                            </figure>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}